import { useState } from 'react'
import SectionCard from './SectionCard'
import SubsectionDivider from './SubsectionDivider'
import FieldRow from './FieldRow'
import { DerivedValueBox, EditableValueBox } from './ValueBox'
import PercentDollarRow, { PercentDollarToggleControl } from './PercentDollarRow'
import InterestDropdown from './InterestDropdown'
import DisclosureToggle from './DisclosureToggle'
import ProjectedInterestPanel from './ProjectedInterestPanel'
import { formatMoney, formatPercent } from './format'

// Tooltip copy is verbatim from files/sow3/spec/canvas-text.27.08.md, "Tool tips 1/2", LOAN COSTS
// block. Not reworded here; if a tooltip changes it changes in Figma first (same rule
// features/arv/fieldLimits.js states for its own drawn strings). Exception: Interest's two headings
// follow Cong's letter 99 (7.3.4), matching the dropdown's own option labels.
// Interest (On Full Loan Amount), LTC and Projected Interest follow Cong's text in letter 115.
// Line breaks (U+2028 in the canvas source) are preserved here as literal \n -- flattening them to
// spaces was itself a defect: it merged "Draw-Based"/"Full Balance" into the following sentence and
// collapsed Title & Escrow's two parallel "label: clause" lines into one run-on string.
// `**word**` = set in bold (700) in the canvas notes' running text (letter 99, 7.3.5; FlipInfoIcon.jsx).
const TOOLTIPS = {
  rehabFinancing:
    'The percentage of the rehab budget financed by the lender. Financed rehab funds are typically released as work is completed and draws are approved.\n\n[Example: At 100%, the lender finances the full eligible rehab budget. At 0%, all rehab costs are paid with your own cash.]',
  interest:
    'Determines how interest accrues during the project.\n\n**On Drawn Funds**\nInterest accrues only on funds that have been released.\n\n**On Full Loan Amount**\nInterest accrues on the total loan amount from the start, including rehab funds that have not yet been drawn.\n\nSee **Projected Interest** for the estimated total based on the selected method and project timeline.',
  ltc: '**Loan-to-Cost (LTC)**\nThe loan amount as a percentage of the purchase price plus rehab budget. A higher LTC generally means less cash needed upfront.\n\n[Loan Amount ÷ (Purchase Price + Rehab Budget)]\n\n**Loan-to-ARV (LTARV)**\nThe loan amount as a percentage of the projected after-repair value. Lenders use it to assess the equity cushion after renovations.\n\n[Loan Amount ÷ ARV]',
  origination:
    'A one-time lender fee for originating the loan, typically calculated as a percentage of the loan amount and charged at closing.',
  lenderFees: 'Additional lender charges, such as appraisal, underwriting, document, or processing fees.',
  projectedInterest:
    'Estimated interest over the project timeline, based on your loan terms. When interest is charged on drawn funds, the estimate accounts for projected rehab draws rather than charging interest on those funds from the start.',
}

// scenario/onFieldChange/onToggleChange/computed: see README.md ("no save endpoint yet", "the
// computed prop contract"). onScheduleChange is the expanded panel's array-slot writer.
export default function LoanCostsSection({ scenario, computed, onFieldChange, onToggleChange, onScheduleChange }) {
  const [detailsExpanded, setDetailsExpanded] = useState(false)
  const s = scenario ?? {}
  const toggles = s.toggles ?? {}

  return (
    <SectionCard number={1} title="Loan Costs" total={formatMoney(computed?.headerLoanCost)}>
      <div className="flex flex-col">
        <FieldRow label="Terms" weight="bold" color="text-brand-secondary">
          <DerivedValueBox empty ariaLabel="Terms" />
        </FieldRow>

        <FieldRow label="Loan Amount">
          <DerivedValueBox value={formatMoney(computed?.loan_amount)} ariaLabel="Loan Amount" />
        </FieldRow>

        <div className="mt-[8px]">
          <FieldRow
            label="Down Payment"
            control={
              <PercentDollarToggleControl
                toggleValue={toggles.financed_down_payment}
                onToggleChange={(v) => onToggleChange('financed_down_payment', v)}
                ariaLabel="Down Payment %"
              />
            }
          >
            <PercentDollarRow
              percentValue={String(s.financed_down_payment1 ?? '')}
              onPercentChange={(v) => onFieldChange('financed_down_payment1', v)}
              percentAriaLabel="Down Payment %"
              dollarValue={String(s.financed_down_payment2 ?? '')}
              onDollarChange={(v) => onFieldChange('financed_down_payment2', v)}
              dollarAriaLabel="Down Payment $"
              dollarComputedValue={formatMoney(computed?.downPaymentDollar)}
              toggleValue={toggles.financed_down_payment}
            />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow label="Rehab Financing %" tooltip={TOOLTIPS.rehabFinancing}>
            <PercentDollarRow
              percentValue={String(s.rehab_financed ?? '')}
              onPercentChange={(v) => onFieldChange('rehab_financed', v)}
              percentAriaLabel="Rehab Financing %"
              dollarAriaLabel="Rehab Financing $"
              dollarComputedValue={formatMoney(computed?.rehabFinancedDollar)}
            />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow
            label="Interest"
            tooltip={TOOLTIPS.interest}
            control={<InterestDropdown value={s.interest_type ?? 'Non-Dutch'} onChange={(v) => onFieldChange('interest_type', v)} />}
          >
            <EditableValueBox
              value={String(s.interest_rate ?? '')}
              onChange={(v) => onFieldChange('interest_rate', v)}
              suffix="%"
              ariaLabel="Interest Rate"
            />
          </FieldRow>
        </div>

        {/* One decimal (letter 99, 6.4): 93.61% -> 93.6%. */}
        <div className="mt-[8px]">
          <FieldRow label="LTC & LTARV" tooltip={TOOLTIPS.ltc}>
            <DerivedValueBox value={formatPercent(computed?.ltc, 1)} hug ariaLabel="LTC" />
            <span className="w-px h-[16px] bg-brand-fieldBdr shrink-0" aria-hidden="true" />
            <DerivedValueBox value={formatPercent(computed?.ltarv, 1)} hug ariaLabel="LTARV" />
          </FieldRow>
        </div>

        <SubsectionDivider testId="loan-terms-divider" className="mt-[8px] mb-[7px]" />

        {/* Zero gap to the next row (spec 1.8: subsection heading -> next row pitch is 32, the row
            height itself, not 32+8) -- no wrapping margin here, matching Terms -> Loan Amount above. */}
        <FieldRow label="Fees" weight="bold" color="text-brand-secondary">
          <DerivedValueBox empty ariaLabel="Fees" />
        </FieldRow>

        <FieldRow
          label="Origination Fee"
          tooltip={TOOLTIPS.origination}
          control={
            <PercentDollarToggleControl
              toggleValue={toggles.origination}
              onToggleChange={(v) => onToggleChange('origination', v)}
              ariaLabel="Origination Fee %"
            />
          }
        >
          <PercentDollarRow
            percentValue={String(s.origination_points0 ?? '')}
            onPercentChange={(v) => onFieldChange('origination_points0', v)}
            percentAriaLabel="Origination Fee %"
            dollarValue={String(s.origination_points1 ?? '')}
            onDollarChange={(v) => onFieldChange('origination_points1', v)}
            dollarAriaLabel="Origination Fee $"
            dollarComputedValue={formatMoney(computed?.originationFee)}
            toggleValue={toggles.origination}
          />
        </FieldRow>

        <div className="mt-[8px]">
          <FieldRow label="Lender Fees" tooltip={TOOLTIPS.lenderFees}>
            <EditableValueBox money
              value={String(s.service_fees ?? '')}
              onChange={(v) => onFieldChange('service_fees', v)}
              ariaLabel="Lender Fees"
            />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow
            label="Projected Interest"
            tooltip={TOOLTIPS.projectedInterest}
            control={
              <DisclosureToggle
                collapsedLabel="View details"
                expandedLabel="Hide details"
                expanded={detailsExpanded}
                onToggle={() => setDetailsExpanded((v) => !v)}
              />
            }
          >
            <DerivedValueBox value={formatMoney(computed?.est_total_interests)} ariaLabel="Projected Interest" />
          </FieldRow>
          {/* Total divider spans the FULL content width here (spec 4.2: x 4828 -> 5260, the board's
              own content width), not the value-column-only 88px the two Acquisition Costs totals
              use (spec 1.7's general rule) -- the per-instance measurement in section 4.2 overrides
              the general token where the two disagree, per spec 0's own methodology. */}
          {detailsExpanded ? (
            <>
              <div className="mt-[8px] h-[2px] bg-brand-fieldBdr" />
              <ProjectedInterestPanel
                scenario={s}
                computed={computed}
                onFieldChange={onFieldChange}
                onScheduleChange={onScheduleChange}
              />
            </>
          ) : null}
        </div>
      </div>
    </SectionCard>
  )
}
